"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ChatModelConfig = exports.defaultSelectedContexts = exports.copilotChatModels = exports.internalDefaultModel = exports.defaultModel = exports.copilotModelConfigs = exports.allCopilotTools = exports.copilotToolConfigs = exports.copilotContextConfigs = exports.CopilotGpt5Models = exports.ChatMessageTool = exports.KnowledgeBaseContextValues = exports.CopilotActionKeys = exports.isActionGenerationChatMessage = exports.isHumanChatMessage = exports.AutopilotRunType = exports.BriefStatusSchema = void 0;
const lodash_1 = require("lodash");
const zod_1 = require("zod");
exports.BriefStatusSchema = zod_1.z.enum([
    "generating",
    "ready",
    "ready_to_execute",
    "working",
    "completed",
    "ambiguous",
    "impossible",
    "discard",
    "failed",
    "stopped",
]);
exports.AutopilotRunType = zod_1.z.enum(["autopilot-v0", "skipper", "orbit"]);
const isHumanChatMessage = (message) => {
    return message.meta_data.type === "human";
};
exports.isHumanChatMessage = isHumanChatMessage;
const isActionGenerationChatMessage = (message) => {
    return message.meta_data.type === "action_generation";
};
exports.isActionGenerationChatMessage = isActionGenerationChatMessage;
// Note: the keys in here MUST match the available keys in the autopilot_execute_step prompt. Keep
// them in sync! Also update generateActions.ts:knownActionPaths if you change keys here.
exports.CopilotActionKeys = [
    "rewriteProjectDescription",
    "rewriteProjectProperties",
    "rewriteDesignators",
    "rewriteComponents",
    "rewriteComponentProperties",
    "rewriteLayoutRules",
    "rewriteProjectNets",
    "rewriteConnections",
    "iteratePlacements",
    "iterateRouting",
];
exports.KnowledgeBaseContextValues = ["project", "user", "system"];
exports.ChatMessageTool = zod_1.z.enum([
    "web",
    "calculator",
    "library",
    "help",
    "file",
    "datasheet",
    "code",
    "image",
    "simulator",
    "bom",
    "schematic",
    "layout",
    "review",
    "action",
    "knowledge",
    "plan",
    "message",
    "export",
    "execution_step_message",
]);
exports.CopilotGpt5Models = [
    "gpt-5",
    "gpt-5.1",
    "gpt-5.1-none",
    "gpt-5.2",
    "gpt-5-high",
    "gpt-5-mini",
    "gpt-5-nano",
    "next-gen-beta",
    "advanced-gpt5",
    "general-gpt5",
    "gpt-5-minimal",
    "gpt-5-mini-low-verbosity-min-reason",
    "gpt-5-mini-med-verbosity-high-reason",
    "gpt-5-mini-low-verbosity-med-reason",
    "gpt-5-mini-low-verbosity-high-reason",
    "autopilot-simple",
    "autopilot-complex",
];
// This is the configuration for the contexts that we use in the chat
exports.copilotContextConfigs = {
    project: {
        key: "project",
        name: "Project",
        description: "Use the project context",
        detailedDescription: "Using the project context allows the AI to understand the project you are working on",
    },
    chatHistory: {
        key: "chatHistory",
        name: "Chat History",
        description: "Use the chat history context",
        detailedDescription: "Using the chat history context allows the AI to understand the full conversation history",
    },
};
// This is the configuration for the tools that we use in the chat
// this is the order in the UI
exports.copilotToolConfigs = {
    schematic: {
        key: "schematic",
        name: "Schematic",
        description: "The Project's Schematic",
        canAutoSelect: true,
        visibility: "visible",
    },
    layout: {
        key: "layout",
        name: "PCB Layout",
        description: "The Project's PCB Layout",
        canAutoSelect: true,
        visibility: "visible",
    },
    bom: {
        key: "bom",
        name: "BOM",
        description: "The Project's Bill of Materials (BOM)",
        canAutoSelect: true,
        visibility: "visible",
    },
    library: {
        key: "library",
        name: "Library",
        description: "Search the library for components",
        canAutoSelect: true,
        visibility: "visible",
    },
    datasheet: {
        key: "datasheet",
        name: "Datasheet",
        description: "Get information from component datasheets",
        canAutoSelect: true,
        visibility: "visible",
    },
    review: {
        key: "review",
        name: "Review (ERC/DRC)",
        description: "Use AI ERC and DRC to review the schematic and layout",
        canAutoSelect: true,
        visibility: "visible",
    },
    simulator: {
        key: "simulator",
        name: "Simulator (Beta)",
        description: "Use Ngspice to run simulations and analysis",
        canAutoSelect: true,
        visibility: "internal",
    },
    calculator: {
        key: "calculator",
        name: "Calculator",
        description: "Use the calculator for math problems",
        canAutoSelect: true,
        visibility: "visible",
    },
    code: {
        key: "code",
        name: "Code Interpreter",
        description: "Use the code interpreter to execute Python code",
        canAutoSelect: true,
        visibility: "visible",
    },
    file: {
        key: "file",
        name: "File",
        description: "Access the content of any file",
        canAutoSelect: true,
        visibility: "visible",
    },
    web: {
        key: "web",
        name: "Web",
        description: "Search the web for information",
        canAutoSelect: true,
        visibility: "visible",
    },
    help: {
        key: "help",
        name: "Help",
        description: "Learn how to use Flux",
        canAutoSelect: true,
        visibility: "visible",
    },
    image: {
        key: "image",
        name: "Image",
        description: "Analyze images",
        canAutoSelect: false,
        visibility: "hidden",
    },
    action: {
        key: "action",
        name: "Action",
        description: "Determine what actions to take next",
        canAutoSelect: true,
        visibility: "hidden",
    },
    knowledge: {
        key: "knowledge",
        name: "Knowledge Base",
        description: "Query the knowledge base for specific information",
        canAutoSelect: true,
        visibility: "hidden",
    },
    plan: {
        key: "plan",
        name: "Plan",
        description: "Generate an execution plan for the user's request",
        canAutoSelect: true,
        visibility: "hidden",
    },
    message: {
        key: "message",
        name: "Message",
        description: "Send a message to the user with optional rewriting",
        canAutoSelect: true,
        visibility: "hidden",
    },
    export: {
        key: "export",
        name: "Export",
        description: "Create files or export project data in various formats",
        canAutoSelect: true,
        visibility: "internal",
    },
    execution_step_message: {
        key: "execution_step_message",
        name: "Execution Step",
        description: "Send a message during execution step processing",
        canAutoSelect: false,
        visibility: "hidden",
    },
};
exports.allCopilotTools = Object.keys(exports.copilotToolConfigs);
// This is the configuration for the models that we use in the chat
// open ai pricing via https://openai.com/api/pricing/
// the order here determines the order in the UI!!!
exports.copilotModelConfigs = {
    // next gen models (internal for now)
    // GPT-5 models
    "gpt-5": {
        visibility: "internal",
        name: "GPT-5",
        model: "gpt-5",
        modelNameWithVersion: "gpt-5-2025-08-07",
        vendor: "openAI",
        description: "GPT-5",
        detailedDescription: "no additional settings",
        langsmithPromptKey: "chat_main_prompt_v3",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 0.0,
            costPerMillionOutputTokensInUSD: 0.0,
            costPerImageInUSD: 0.0,
            creditsVendorKey: "openAiUsage",
        },
        contextWindowSize: 400000,
    },
    "gpt-5.1": {
        visibility: "internal",
        name: "GPT-5.1",
        model: "gpt-5.1",
        modelNameWithVersion: "gpt-5.1-2025-11-13",
        vendor: "openAI",
        description: "GPT-5.1 (latest)",
        detailedDescription: "Latest GPT-5 model with default reasoning settings",
        langsmithPromptKey: "chat_main_prompt_v3",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 1.25,
            costPerMillionOutputTokensInUSD: 10.0,
            costPerImageInUSD: 0.00475,
            creditsVendorKey: "openAiUsage",
        },
        contextWindowSize: 400000,
    },
    "gpt-5.2": {
        visibility: "internal",
        name: "Autopilot",
        model: "gpt-5.2",
        modelNameWithVersion: "gpt-5.2-2025-12-11",
        vendor: "openAI",
        description: "GPT-5.2",
        detailedDescription: "Our advanced autonomous task model",
        langsmithPromptKey: "chat_main_prompt_v3",
        verbosity: "low",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 1.25,
            costPerMillionOutputTokensInUSD: 10.0,
            costPerImageInUSD: 0.00475,
            creditsVendorKey: "openAiUsage",
        },
        contextWindowSize: 400000,
    },
    "gpt-5-high": {
        visibility: "internal",
        name: "GPT-5 high reasoning",
        model: "gpt-5-high",
        modelNameWithVersion: "gpt-5-2025-08-07",
        vendor: "openAI",
        description: "GPT-5, high",
        detailedDescription: "reasoning: high",
        langsmithPromptKey: "chat_main_prompt_v3",
        reasoningEffort: "high",
        verbosity: "high",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 0.0,
            costPerMillionOutputTokensInUSD: 0.0,
            costPerImageInUSD: 0.0,
            creditsVendorKey: "openAiUsage",
        },
        contextWindowSize: 400000,
    },
    "gpt-5-minimal": {
        visibility: "internal",
        name: "GPT-5 minimal",
        model: "gpt-5-minimal",
        modelNameWithVersion: "gpt-5-2025-08-07",
        vendor: "openAI",
        description: "GPT-5, minimal",
        detailedDescription: "reasoning: minimal, verbosity: low",
        langsmithPromptKey: "chat_main_prompt_v3",
        reasoningEffort: "minimal",
        verbosity: "low",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 0.0,
            costPerMillionOutputTokensInUSD: 0.0,
            costPerImageInUSD: 0.0,
            creditsVendorKey: "openAiUsage",
        },
        contextWindowSize: 400000,
    },
    // GPT-5-mini
    "gpt-5-mini": {
        visibility: "internal",
        name: "GPT-5 Mini",
        model: "gpt-5-mini",
        modelNameWithVersion: "gpt-5-mini-2025-08-07",
        vendor: "openAI",
        description: "GPT-5-mini",
        detailedDescription: "reasoning: medium, verbosity: low",
        langsmithPromptKey: "chat_main_prompt_v3",
        verbosity: "low",
        reasoningEffort: "medium",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 0.0,
            costPerMillionOutputTokensInUSD: 0.0,
            costPerImageInUSD: 0.0,
            creditsVendorKey: "openAiUsage",
        },
        contextWindowSize: 400000,
    },
    // GPT-5-nano
    "gpt-5-nano": {
        model: "gpt-5-nano",
        modelNameWithVersion: "gpt-5-nano-2025-08-07",
        vendor: "openAI",
        name: "GPT-5-nano",
        description: "GPT-5-nano",
        detailedDescription: "reasoning: medium, verbosity: low",
        langsmithPromptKey: "chat_main_prompt_v3",
        verbosity: "low",
        reasoningEffort: "medium",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 0.0,
            costPerMillionOutputTokensInUSD: 0.0,
            costPerImageInUSD: 0.0,
            creditsVendorKey: "openAiUsage",
        },
        visibility: "internal",
        contextWindowSize: 400000,
    },
    o3: {
        visibility: "internal",
        name: "Advanced (prev)",
        model: "o3",
        modelNameWithVersion: "o3-2025-04-16", // Updated version ID
        vendor: "openAI",
        description: "Prev-gen Advanced",
        detailedDescription: "o3: Previous gen Advanced preset",
        langsmithPromptKey: "chat_main_prompt_v3",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 10,
            costPerMillionOutputTokensInUSD: 40.0,
            costPerImageInUSD: 0.00475,
            creditsVendorKey: "openAiUsage",
        },
        contextWindowSize: 200000,
    },
    "o4-mini": {
        model: "o4-mini",
        modelNameWithVersion: "o4-mini-2025-04-16", // Updated version ID
        vendor: "openAI",
        name: "General (prev)",
        description: "General (prev)",
        detailedDescription: "Our previous generation General model.",
        langsmithPromptKey: "chat_main_prompt_v3",
        reasoningEffort: "medium",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 1.1,
            costPerMillionOutputTokensInUSD: 4.4,
            costPerImageInUSD: 0.00475,
            creditsVendorKey: "openAiUsage",
        },
        visibility: "internal",
        contextWindowSize: 200000,
    },
    // Autopilot models
    "autopilot-simple": {
        visibility: "hidden",
        model: "autopilot-simple",
        modelNameWithVersion: "gpt-5.1-2025-11-13",
        vendor: "openAI",
        name: "Autopilot (simple)",
        description: "Our autonomous task model for simple tasks.",
        detailedDescription: "Our autonomous task model for simple tasks.",
        langsmithPromptKey: "chat_main_prompt_v3",
        reasoningEffort: "none",
        verbosity: "low",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 1.25,
            costPerMillionOutputTokensInUSD: 10.0,
            costPerImageInUSD: 0.00475,
            creditsVendorKey: "openAiUsage",
        },
        contextWindowSize: 400000,
    },
    "autopilot-medium": {
        visibility: "hidden",
        model: "autopilot-medium",
        modelNameWithVersion: "o4-mini-2025-04-16", // Updated version ID
        vendor: "openAI",
        name: "Autopilot",
        description: "Our autonomous task model for typical tasks.",
        detailedDescription: "Our autonomous task model for typical tasks.",
        langsmithPromptKey: "chat_main_prompt_v3",
        reasoningEffort: "medium",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 1.1,
            costPerMillionOutputTokensInUSD: 4.4,
            costPerImageInUSD: 0.00475,
            creditsVendorKey: "openAiUsage",
        },
        contextWindowSize: 200000,
    },
    "autopilot-complex": {
        visibility: "hidden",
        model: "autopilot-complex",
        modelNameWithVersion: "gpt-5-mini-2025-08-07",
        vendor: "openAI",
        name: "Autopilot (advanced)",
        description: "Autopilot (advanced)",
        detailedDescription: "Our advanced autonomous task model for complex tasks.",
        langsmithPromptKey: "chat_main_prompt_v3",
        verbosity: "low",
        reasoningEffort: "medium",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 0.0,
            costPerMillionOutputTokensInUSD: 0.0,
            costPerImageInUSD: 0.0,
            creditsVendorKey: "openAiUsage",
        },
        contextWindowSize: 400000,
    },
    // Public models
    "advanced-gpt5": {
        visibility: "visible",
        name: "Advanced",
        model: "advanced-gpt5",
        modelNameWithVersion: "gpt-5-2025-08-07",
        vendor: "openAI",
        description: "Most capable, best at complex tasks",
        detailedDescription: "Our Advanced Reasoning model. Slower, but better at complex tasks.",
        langsmithPromptKey: "chat_main_prompt_v3",
        reasoningEffort: "high",
        verbosity: "high",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 0.0,
            costPerMillionOutputTokensInUSD: 0.0,
            costPerImageInUSD: 0.0,
            creditsVendorKey: "openAiUsage",
        },
        contextWindowSize: 400000,
    },
    "general-gpt5": {
        visibility: "visible",
        name: "General",
        model: "general-gpt5",
        modelNameWithVersion: "gpt-5-2025-08-07",
        vendor: "openAI",
        description: "Balanced performance for most tasks",
        detailedDescription: "Our Advanced Reasoning model. Fast and accurate.",
        langsmithPromptKey: "chat_main_prompt_v3",
        reasoningEffort: "medium",
        verbosity: "low",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 0.0,
            costPerMillionOutputTokensInUSD: 0.0,
            costPerImageInUSD: 0.0,
            creditsVendorKey: "openAiUsage",
        },
        contextWindowSize: 400000,
    },
    "gpt-5.1-none": {
        visibility: "visible",
        name: "Speedy",
        model: "gpt-5.1-none",
        modelNameWithVersion: "gpt-5.1-2025-11-13",
        vendor: "openAI",
        description: "Fastest, best for simple tasks",
        detailedDescription: "Our Speedy model. Faster, but less accurate than our General model.",
        langsmithPromptKey: "chat_main_prompt_v3",
        reasoningEffort: "none",
        verbosity: "low",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 1.25,
            costPerMillionOutputTokensInUSD: 10.0,
            costPerImageInUSD: 0.00475,
            creditsVendorKey: "openAiUsage",
        },
        contextWindowSize: 400000,
    },
    // previous gen models
    "gpt-4.1": {
        visibility: "hidden",
        name: "Speedy",
        model: "gpt-4.1",
        modelNameWithVersion: "gpt-4.1-2025-04-14", // Keeping the same version ID for now
        vendor: "openAI",
        description: "Previous generation Speedy model",
        detailedDescription: "Our Speedy model (previous generation). Faster, but less accurate.",
        langsmithPromptKey: "chat_main_prompt_v3",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 2.0,
            costPerMillionOutputTokensInUSD: 8.0,
            costPerImageInUSD: 0.00475,
            creditsVendorKey: "openAiUsage",
        },
        contextWindowSize: 1047576,
    },
    o1: {
        model: "o1",
        modelNameWithVersion: "o1-2024-12-17",
        vendor: "openAI",
        name: "Advanced Reasoning",
        description: "Slower, more capable",
        detailedDescription: "Our Advanced Reasoning model. Slower, but better at complex tasks",
        langsmithPromptKey: "chat_main_prompt_v4",
        capabilities: {
            supportsStreaming: false,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 15.0,
            costPerMillionOutputTokensInUSD: 60.0,
            costPerImageInUSD: 0.01013,
            creditsVendorKey: "openAiUsage",
        },
        visibility: "hidden",
        contextWindowSize: 200000,
    },
    "o3-mini": {
        model: "o3-mini",
        modelNameWithVersion: "o3-mini-2025-01-31",
        vendor: "openAI",
        name: "General (prev)",
        description: "Fast and capable",
        detailedDescription: "Our Advanced Reasoning model. Fast and accurate",
        langsmithPromptKey: "chat_main_prompt_v3",
        reasoningEffort: "medium",
        capabilities: {
            supportsStreaming: true,
            supportsVision: false,
        },
        cost: {
            costPerMillionInputTokensInUSD: 1.1,
            costPerMillionOutputTokensInUSD: 4.4,
            costPerImageInUSD: 0.01013,
            creditsVendorKey: "openAiUsage",
        },
        visibility: "hidden",
        contextWindowSize: 200000,
    },
    "gpt-4o": {
        model: "gpt-4o",
        modelNameWithVersion: "gpt-4o-2024-08-06",
        vendor: "openAI",
        name: "Speedy",
        description: "Faster, less capable",
        detailedDescription: "Our Speedy model. Faster, but less accurate than our General model.",
        langsmithPromptKey: "chat_main_prompt_v3",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 2.5,
            costPerMillionOutputTokensInUSD: 10,
            costPerImageInUSD: 0.001913,
            creditsVendorKey: "openAiUsage",
        },
        visibility: "hidden",
        contextWindowSize: 128000,
    },
    "gpt-4o-mini": {
        model: "gpt-4o-mini",
        modelNameWithVersion: "gpt-4o-mini-2024-07-18",
        vendor: "openAI",
        name: "GPT 4o mini",
        description: "GPT 4o mini: no reasoning, fast, cheap",
        detailedDescription: "GPT 4o mini: no reasoning, fast, cheap",
        langsmithPromptKey: "n/a",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 0.15,
            costPerMillionOutputTokensInUSD: 0.6,
            creditsVendorKey: "openAiUsage",
        },
        visibility: "hidden",
        contextWindowSize: 128000,
    },
    "next-gen-beta": {
        model: "next-gen-beta",
        modelNameWithVersion: "gpt-5-2025-08-07",
        vendor: "openAI",
        name: "Next gen (beta)",
        description: "Our most advanced next generation model",
        detailedDescription: "Our new most advanced model",
        langsmithPromptKey: "chat_main_prompt_v3",
        reasoningEffort: "minimal",
        verbosity: "low",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 0.0,
            costPerMillionOutputTokensInUSD: 0.0,
            costPerImageInUSD: 0.0,
            creditsVendorKey: "openAiUsage",
        },
        visibility: "hidden",
        contextWindowSize: 400000,
    },
    "gpt-5-mini-low-verbosity-min-reason": {
        model: "gpt-5-mini-low-verbosity-min-reason",
        modelNameWithVersion: "gpt-5-mini-2025-08-07",
        vendor: "openAI",
        name: "GPT-5 Mini reasoning: minimal, verbosity: low",
        description: "reasoning: minimal, verbosity: low",
        detailedDescription: "reasoning: minimal, verbosity: low",
        langsmithPromptKey: "chat_main_prompt_v3",
        verbosity: "low",
        reasoningEffort: "minimal",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 0.0,
            costPerMillionOutputTokensInUSD: 0.0,
            costPerImageInUSD: 0.0,
            creditsVendorKey: "openAiUsage",
        },
        visibility: "hidden",
        contextWindowSize: 400000,
    },
    "gpt-5-mini-med-verbosity-high-reason": {
        model: "gpt-5-mini-med-verbosity-high-reason",
        modelNameWithVersion: "gpt-5-mini-2025-08-07",
        vendor: "openAI",
        name: "GPT-5 Mini reasoning: high, verbosity: medium",
        description: "reasoning: high, verbosity: medium",
        detailedDescription: "reasoning: high, verbosity: medium",
        langsmithPromptKey: "chat_main_prompt_v3",
        verbosity: "medium",
        reasoningEffort: "high",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 0.0,
            costPerMillionOutputTokensInUSD: 0.0,
            costPerImageInUSD: 0.0,
            creditsVendorKey: "openAiUsage",
        },
        visibility: "hidden",
        contextWindowSize: 400000,
    },
    "gpt-5-mini-low-verbosity-med-reason": {
        model: "gpt-5-mini-low-verbosity-med-reason",
        modelNameWithVersion: "gpt-5-mini-2025-08-07",
        vendor: "openAI",
        name: "GPT-5 Mini reasoning: medium, verbosity: low",
        description: "reasoning: medium, verbosity: low",
        detailedDescription: "reasoning: medium, verbosity: low",
        langsmithPromptKey: "chat_main_prompt_v3",
        verbosity: "low",
        reasoningEffort: "medium",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 0.0,
            costPerMillionOutputTokensInUSD: 0.0,
            costPerImageInUSD: 0.0,
            creditsVendorKey: "openAiUsage",
        },
        visibility: "hidden",
        contextWindowSize: 400000,
    },
    "gpt-5-mini-low-verbosity-high-reason": {
        model: "gpt-5-mini-low-verbosity-high-reason",
        modelNameWithVersion: "gpt-5-mini-2025-08-07",
        vendor: "openAI",
        name: "GPT-5 Mini reasoning: high, verbosity: low",
        description: "reasoning: high, verbosity: low",
        detailedDescription: "reasoning: high, verbosity: low",
        langsmithPromptKey: "chat_main_prompt_v3",
        verbosity: "low",
        reasoningEffort: "high",
        capabilities: {
            supportsStreaming: true,
            supportsVision: true,
        },
        cost: {
            costPerMillionInputTokensInUSD: 0.0,
            costPerMillionOutputTokensInUSD: 0.0,
            costPerImageInUSD: 0.0,
            creditsVendorKey: "openAiUsage",
        },
        visibility: "hidden",
        contextWindowSize: 400000,
    },
};
// This populates the client UI with the available models
exports.defaultModel = "general-gpt5";
exports.internalDefaultModel = "general-gpt5";
exports.copilotChatModels = (0, lodash_1.values)(exports.copilotModelConfigs).map((config) => ({
    model: config.model,
    name: config.name,
    description: config.description,
    visibility: config.visibility,
}));
exports.defaultSelectedContexts = ["project", "chatHistory"];
const gptModels = ["gpt-4o", "gpt-4.1"];
const reasoningModels = ["o4-mini", "o3", "o3-mini", "o1"];
exports.ChatModelConfig = zod_1.z.discriminatedUnion("model", [
    zod_1.z.object({
        model: zod_1.z.enum(gptModels),
        temperature: zod_1.z.number().min(0).max(2).optional(),
        verbosity: zod_1.z.enum(["high", "medium", "low"]).optional(),
    }),
    zod_1.z.object({
        model: zod_1.z.enum(reasoningModels),
        reasoningEffort: zod_1.z.enum(["low", "medium", "high"]).optional(),
    }),
    zod_1.z.object({
        model: zod_1.z.enum(["gpt-5.1", "gpt-5.1-none"]),
        reasoningEffort: zod_1.z.enum(["none", "low", "medium", "high"]).optional(),
        verbosity: zod_1.z.enum(["high", "medium", "low"]).optional(),
    }),
    zod_1.z.object({
        model: zod_1.z.enum(["gpt-5", "gpt-5-minimal", "gpt-5-high", "gpt-5-nano"]),
        reasoningEffort: zod_1.z.enum(["minimal", "low", "medium", "high"]).optional(),
        verbosity: zod_1.z.enum(["high", "medium", "low"]).optional(),
    }),
    zod_1.z.object({
        model: zod_1.z.enum(["gpt-5.2", "gpt-5.2-pro", "gpt-5.1-codex-max"]),
        reasoningEffort: zod_1.z.enum(["minimal", "low", "medium", "high"]).optional(),
        verbosity: zod_1.z.enum(["high", "medium", "low"]).optional(),
    }),
]);
//# sourceMappingURL=structure.js.map